import { LOGIN_SUCCESS, LOGOUT_REQUEST } from "./constants/actionTypes";

// O SDK do Sentry (~266 KiB) não entra no bundle inicial: é carregado com import() dinâmico
// depois do `load` da página (ou antes, se um erro de render precisar ser reportado).
// Erros globais que acontecem antes do SDK iniciar ficam numa fila curta e são reenviados.
const MAX_BUFFERED_ERRORS = 20;

let sentry = null;
let sentryPromise = null;
let pendingTag;
let hasPendingTag = false;
let bufferedErrors = [];

const isEnabled = () => process.env.REACT_APP_GLITCHTIP_ENABLED === "true";

const bufferError = event => {
  if (bufferedErrors.length >= MAX_BUFFERED_ERRORS) return;
  bufferedErrors.push(event.reason || event.error || event.message);
};

const startBuffering = () => {
  window.addEventListener("error", bufferError);
  window.addEventListener("unhandledrejection", bufferError);
};

const stopBuffering = () => {
  window.removeEventListener("error", bufferError);
  window.removeEventListener("unhandledrejection", bufferError);
};

const loadSentry = () => {
  if (!sentryPromise) {
    sentryPromise = import("@sentry/browser").then(Sentry => {
      Sentry.init({
        dsn: process.env.REACT_APP_GLITCHTIP_DSN,
        tunnel: `${process.env.REACT_APP_RECARGA_API_URL}/monitoring/envelope`,
        environment: process.env.REACT_APP_NODE_ENV,
        release: process.env.REACT_APP_VERSION || "dev",
        autoSessionTracking: false, // GlitchTip não suporta session tracking
      });
      sentry = Sentry;

      stopBuffering();
      if (hasPendingTag) Sentry.setTag("msisdn", pendingTag);
      bufferedErrors.forEach(error => Sentry.captureException(error));
      bufferedErrors = [];

      return Sentry;
    });
    // Se o chunk falhar (rede), libera para uma nova tentativa no próximo erro reportado.
    sentryPromise.catch(() => {
      sentryPromise = null;
    });
  }

  return sentryPromise;
};

export const initMonitoring = () => {
  if (!isEnabled()) return;

  startBuffering();

  const start = () => loadSentry().catch(() => {});

  if (document.readyState === "complete") {
    setTimeout(start, 0);
  } else {
    window.addEventListener("load", () => setTimeout(start, 0), { once: true });
  }
};

export const captureException = (error, context) => {
  if (!isEnabled()) return;

  loadSentry()
    .then(Sentry => Sentry.captureException(error, context))
    .catch(() => {});
};

export const monitoringMiddleware = store => next => action => {
  const result = next(action);

  if (action.type === LOGIN_SUCCESS || action.type === LOGOUT_REQUEST) {
    const { identifier } = store.getState().sessionState;
    const msisdn = identifier || undefined;

    if (sentry) {
      sentry.setTag("msisdn", msisdn);
    } else {
      pendingTag = msisdn;
      hasPendingTag = true;
    }
  }

  return result;
};
